import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { EnrollmentService } from "@core-sub/services/enrollment.service";
import { WindowSessionStorageNamesEn } from "@core-sub/WindowSessionStorageNamesEn";
import { BiStore } from "@globals/classes/BiStore";
import { EnrollmentTokenModelExt } from "@models-sub/ext/EnrollmentTokenModelExt";
import { SignUpEnrolleeDtoExt } from "@models-sub/Ext/SignUpEnrolleeDtoExt";
import { EnrollmentAddressDtoExt } from "@shared-sub/components/en-address-selector/EnrollmentAddressDtoExt";
import { catchError, map, of, Subject, tap } from "rxjs";

interface EnrollmentSharedData {
  address: EnrollmentAddressDtoExt;
}

@Injectable({
  providedIn: "root"
})
export class EnrollmentStepsSharedService extends BiStore<EnrollmentSharedData> {
  private readonly onNewUserSignedUp = new Subject<boolean>();
  public readonly onNewUserSignedUp$ = this.onNewUserSignedUp.asObservable();

  private readonly onExistingEnrolleeFound = new Subject<EnrollmentTokenModelExt>();
  public readonly onExistingEnrolleeFound$ = this.onExistingEnrolleeFound.asObservable();

  private readonly enrollmentService = inject(EnrollmentService);

  constructor() {
    super(
      sessionStorage.getItem(WindowSessionStorageNamesEn.enrollmentAddressAndName)
        ? JSON.parse(sessionStorage.getItem(WindowSessionStorageNamesEn.enrollmentAddressAndName))
        : {
            address: undefined
          }
    );
  }

  public refreshOperatorInfo(countryId: number) {
    return this.enrollmentService.getOperatorInfo(countryId).pipe(
      tap(data => {
        this.state.next({ ...this.state.value, address: data.address });
      })
    );
  }

  public setStateAndUseSessionStorage(stateData: EnrollmentSharedData) {
    this.state.next({ ...this.state.value, address: stateData.address });
    sessionStorage.setItem(WindowSessionStorageNamesEn.enrollmentAddressAndName, JSON.stringify({ address: stateData.address }));
  }

  public signUpEnrollee(dto: SignUpEnrolleeDtoExt, address: EnrollmentAddressDtoExt, enrolleeName: string) {
    this.setState({
      ...this.getCurrentStateValue(),
      address: { ...address, enrolleeName }
    });

    return this.enrollmentService.signUp(dto).pipe(
      map(() => false),
      catchError((err: HttpErrorResponse) => {
        // 409 = conflict => enrollee already signed up
        if (err.status === 409) return of(true);
        throw err;
      }),
      tap(hadConflict => this.onNewUserSignedUp.next(hadConflict))
    );
  }

  public clearAddress() {
    this.state.next({ ...this.state.value, address: undefined });
    sessionStorage.removeItem(WindowSessionStorageNamesEn.enrollmentAddressAndName);
  }

  public notifyExistingEnrolleeFound(token: EnrollmentTokenModelExt) {
    this.onExistingEnrolleeFound.next(token);
  }
}
